import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, X } from "lucide-react";
import { useIsMobile } from "./mobile/DesktopOnlyScreen";

const DISPLAY_MS = 5000;

/**
 * Top-anchored, larger-text in-app "new message" banner for mobile —
 * replaces the generic bottom-right sonner toast for this one notification
 * type (desktop keeps that toast unchanged). Fired by ChatContext via a
 * `chat:new-message-banner` window event whenever unread count grows while
 * the user isn't already on /messages. Auto-dismisses after 5s.
 */
export default function MobileNewMessageBanner() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [item, setItem] = useState(null); // {title, body}
  const timerRef = useRef(null);

  useEffect(() => {
    const onEvt = (e) => {
      setItem(e.detail || null);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setItem(null), DISPLAY_MS);
    };
    window.addEventListener("chat:new-message-banner", onEvt);
    return () => {
      window.removeEventListener("chat:new-message-banner", onEvt);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  if (!isMobile || !item) return null;

  return (
    <div
      className="fixed top-0 inset-x-0 z-[300] flex justify-center px-3 pt-[calc(0.75rem+env(safe-area-inset-top))]"
      data-testid="mobile-new-message-banner"
    >
      <div
        onClick={() => { setItem(null); navigate("/messages"); }}
        className="w-full max-w-md rounded-2xl shadow-2xl px-4 py-4 flex items-start gap-3 active:opacity-95 cursor-pointer"
        style={{ backgroundColor: "#0B1E3F", border: "1px solid rgba(255,255,255,0.12)" }}
      >
        <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(59,130,246,0.22)" }}>
          <MessageSquare className="h-6 w-6" color="#60A5FA" />
        </div>
        <div className="flex-1 min-w-0 pt-0.5">
          <div className="text-[16px] font-bold text-white">{item.title || "New message"}</div>
          <div className="text-[14px] mt-0.5 leading-snug" style={{ color: "#cbd5e1" }}>{item.body}</div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); setItem(null); }}
          data-testid="mobile-new-message-banner-close"
          aria-label="Dismiss"
          className="h-7 w-7 rounded-md flex items-center justify-center shrink-0"
        >
          <X className="h-4 w-4" color="#94a3b8" />
        </button>
      </div>
    </div>
  );
}
